import * as React from 'react';
import { Link } from '@tanstack/react-router';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, ChevronDown, MoreHorizontal, Plus } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Profile, ProfilePositionType } from '../types';
import { ProfilesService } from '../services/profiles-service';

const positionTypeColors: Record<ProfilePositionType, string> = {
  executive_team: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  board_member: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300',
};

const positionTypeLabels: Record<ProfilePositionType, string> = {
  executive_team: 'Executive Team',
  board_member: 'Board Member',
};

interface ProfileTableProps {
  data: Profile[];
  onPositionTypeFilterChange?: (positionType: string | undefined) => void;
  onShowActiveFilterChange?: (showActive: boolean | undefined) => void;
}

export function ProfileTable({ 
  data,
}: ProfileTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const columns: ColumnDef<Profile>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'profile_full_name',
      header: 'Name',
      cell: ({ row }) => {
        const profile = row.original;
        const nameParts = profile.profile_full_name.split(' ');
        const initials = nameParts.length > 1 
          ? `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`
          : nameParts[0].substring(0, 2).toUpperCase();
        
        // Helper function to get the correct image URL
        const getImageUrl = (path: string | null | undefined) => {
          if (!path) return undefined;
          if (path.startsWith('http')) return path;
          
          // Check if we're in development or production
          const isDevelopment = process.env.NODE_ENV === 'development';
          
          if (isDevelopment) {
            // In development, use the local path
            return path;
          } else {
            // In production, use the API endpoint
            return `/api/assets${path}`;
          }
        };
        
        return (
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={getImageUrl(profile.profile_photo)} alt={profile.profile_full_name} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="font-medium">{profile.profile_full_name}</span>
              <span className="text-muted-foreground text-sm">
                {profile.profile_title}
              </span>
            </div>
          </div>
        );
      },
      size: 250,
    },
    {
      accessorKey: 'profile_position_type',
      header: 'Position Type',
      cell: ({ row }) => {
        const positionType = row.getValue('profile_position_type') as ProfilePositionType;
        return (
          <Badge className={positionTypeColors[positionType]} variant="outline">
            {positionTypeLabels[positionType]}
          </Badge>
        );
      },
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: 'profile_summary',
      header: 'Summary',
      cell: ({ row }) => {
        const summary = row.getValue('profile_summary') as string | undefined;
        return (
          <div className="text-sm max-w-md truncate">
            {summary || <span className="text-muted-foreground">No summary</span>}
          </div>
        );
      },
    },
    {
      accessorKey: 'profile_order_weight',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Order
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const weight = row.getValue('profile_order_weight') as number;
        return <div className="text-center">{weight}</div>;
      },
    },
    {
      accessorKey: 'profile_show_active',
      header: 'Status',
      cell: ({ row }) => {
        const isActive = row.getValue('profile_show_active') as boolean;
        return (
          <Badge 
            className={isActive 
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
            } 
            variant="outline"
          >
            {isActive ? 'Active' : 'Inactive'}
          </Badge>
        );
      },
      filterFn: (row, id, value) => {
        if (value.includes('true') && value.includes('false')) return true;
        const isActive = row.getValue(id) as boolean;
        if (value.includes('true') && isActive) return true;
        if (value.includes('false') && !isActive) return true;
        return false;
      },
    },
    {
      accessorKey: 'updated_at',
      header: 'Last Updated',
      cell: ({ row }) => {
        const timestamp = row.getValue('updated_at') as number;
        const date = new Date(timestamp * 1000); // Convert Unix timestamp to JS Date
        return (
          <div className="text-sm" title={format(date, 'PPpp')}>
            {formatDistanceToNow(date, { addSuffix: true })}
          </div>
        );
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const profile = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem asChild>
                <Link to="/profiles/$id" params={{ id: profile.id }}>View details</Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to="/profiles/edit/$id" params={{ id: profile.id }}>Edit profile</Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  const newStatus = !profile.profile_show_active;
                  const action = newStatus ? 'activate' : 'deactivate';
                  
                  if (window.confirm(`Are you sure you want to ${action} ${profile.profile_full_name}?`)) {
                    try {
                      await ProfilesService.updateProfile(profile.id, {
                        profile_show_active: newStatus
                      });
                      
                      // Use the browser's built-in alert for simplicity
                      alert(`Profile ${action}d successfully`);
                      
                      // Refresh the page to update the table
                      window.location.reload();
                    } catch (error) {
                      console.error(`Error ${action}ing profile:`, error);
                      alert(`Failed to ${action} profile`);
                    }
                  }
                }}
              >
                {profile.profile_show_active ? 'Deactivate' : 'Activate'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="text-destructive"
                onClick={async () => {
                  if (window.confirm(`Are you sure you want to delete ${profile.profile_full_name}?`)) {
                    try {
                      await ProfilesService.deleteProfile(profile.id);
                      // Use the browser's built-in alert for simplicity
                      alert('Profile deleted successfully');
                      // Refresh the page to update the table
                      window.location.reload();
                    } catch (error) {
                      console.error('Error deleting profile:', error);
                      alert('Failed to delete profile');
                    }
                  }
                }}
              >
                Delete profile
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full">
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center gap-2">
          <Input
            placeholder="Filter profiles..."
            value={(table.getColumn('profile_full_name')?.getFilterValue() as string) ?? ''}
            onChange={(event) =>
              table.getColumn('profile_full_name')?.setFilterValue(event.target.value)
            }
            className="max-w-sm"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Position Type <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(positionTypeLabels).map(([type, label]) => (
                <DropdownMenuCheckboxItem
                  key={type}
                  checked={table
                    .getColumn('profile_position_type')
                    ?.getFilterValue() as string[] | undefined
                    ? (table.getColumn('profile_position_type')?.getFilterValue() as string[]).includes(type)
                    : true
                  }
                  onCheckedChange={(value) => {
                    const filterValues = (table.getColumn('profile_position_type')?.getFilterValue() as string[]) || [];
                    if (value) {
                      table.getColumn('profile_position_type')?.setFilterValue([...filterValues, type]);
                    } else {
                      table.getColumn('profile_position_type')?.setFilterValue(filterValues.filter((val) => val !== type));
                    }
                  }}
                >
                  {label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Status <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuCheckboxItem
                checked={table
                  .getColumn('profile_show_active')
                  ?.getFilterValue() as string[] | undefined
                  ? (table.getColumn('profile_show_active')?.getFilterValue() as string[]).includes('true')
                  : true
                }
                onCheckedChange={(value) => {
                  const filterValues = (table.getColumn('profile_show_active')?.getFilterValue() as string[]) || [];
                  if (value) {
                    if (!filterValues.includes('true')) {
                      table.getColumn('profile_show_active')?.setFilterValue([...filterValues, 'true']);
                    }
                  } else {
                    table.getColumn('profile_show_active')?.setFilterValue(
                      filterValues.filter((val) => val !== 'true')
                    );
                  }
                }}
              >
                Active
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={table
                  .getColumn('profile_show_active')
                  ?.getFilterValue() as string[] | undefined
                  ? (table.getColumn('profile_show_active')?.getFilterValue() as string[]).includes('false')
                  : true
                }
                onCheckedChange={(value) => {
                  const filterValues = (table.getColumn('profile_show_active')?.getFilterValue() as string[]) || [];
                  if (value) {
                    if (!filterValues.includes('false')) {
                      table.getColumn('profile_show_active')?.setFilterValue([...filterValues, 'false']);
                    }
                  } else {
                    table.getColumn('profile_show_active')?.setFilterValue(
                      filterValues.filter((val) => val !== 'false')
                    );
                  }
                }}
              >
                Inactive
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="ml-auto">
            Export
          </Button>
          <Button asChild size="sm">
            <Link to="/profiles/add">
              <Plus className="mr-2 h-4 w-4" />
              Add Profile
            </Link>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No profiles found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end space-x-2 py-4">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} of{' '}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
